// Main Database Integration for ACCTOO
// Auto-detects Electron vs Browser environment

import BrowserDatabaseManager from './BrowserDatabaseManager.js';
import ElectronDatabaseManager from './ElectronDatabaseManager.js';
import { db } from '../firebase.config.js';

class DatabaseManager {
  constructor() {
    this.databaseManager = null;
    this.isInitialized = false;
    this.environment = this.detectEnvironment();
  }

  // Detect environment (Electron vs Browser)
  detectEnvironment() {
    if (typeof window !== 'undefined' && window.electronAPI) {
      return 'electron';
    } else {
      return 'browser';
    }
  }

  // =============================================
  // INITIALIZATION
  // =============================================

  // Initialize database system
  async init(userId, companyId, deviceId) {
    try {
      console.log(`🔄 Initializing ${this.environment} database system...`, { userId, companyId, deviceId });

      // Initialize appropriate database manager
      if (this.environment === 'electron') {
        this.databaseManager = ElectronDatabaseManager;
        await this.databaseManager.init(userId, companyId, deviceId);
      } else {
        this.databaseManager = BrowserDatabaseManager;
        await this.databaseManager.init(userId, companyId, deviceId);
      }

      this.isInitialized = true;
      console.log(`✅ ${this.environment} database system initialized successfully`);

      return { success: true, environment: this.environment };
    } catch (error) {
      console.error(`❌ ${this.environment} database initialization failed:`, error);
      throw error;
    }
  }

  // =============================================
  // BUSINESS DATA OPERATIONS
  // =============================================

  // Generic data operations
  async insert(tableName, data) {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.databaseManager.insert(tableName, data);
    } catch (error) {
      console.error(`❌ Insert failed for ${tableName}:`, error);
      throw error;
    }
  }

  async update(tableName, id, data) {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.databaseManager.update(tableName, id, data);
    } catch (error) {
      console.error(`❌ Update failed for ${tableName}:`, error);
      throw error;
    }
  }

  async select(tableName, filters = {}) {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.databaseManager.select(tableName, filters);
    } catch (error) {
      console.error(`❌ Select failed for ${tableName}:`, error);
      throw error;
    }
  }

  async delete(tableName, id) {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.databaseManager.delete(tableName, id);
    } catch (error) {
      console.error(`❌ Delete failed for ${tableName}:`, error);
      throw error;
    }
  }

  // =============================================
  // BILL NUMBER GENERATION
  // =============================================

  // Generate bill number
  async generateBillNumber(billType) {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.databaseManager.generateBillNumber(billType);
    } catch (error) {
      console.error('❌ Generate bill number failed:', error);
      throw error;
    }
  }

  // =============================================
  // SYNC OPERATIONS
  // =============================================

  // Manual sync trigger
  async sync() {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.databaseManager.sync();
    } catch (error) {
      console.error('❌ Sync failed:', error);
      throw error;
    }
  }

  // Get sync status
  async getSyncStatus() {
    if (!this.isInitialized) {
      return { isOnline: false, syncInProgress: false };
    }

    try {
      return await this.databaseManager.getSyncStatus();
    } catch (error) {
      console.error('❌ Get sync status failed:', error);
      throw error;
    }
  }


  // =============================================
  // UTILITY METHODS
  // =============================================

  // Check if initialized
  isReady() {
    return this.isInitialized;
  }

  // Get current context
  getContext() {
    if (!this.isInitialized) {
      return { environment: this.environment };
    }

    try {
      return this.databaseManager.getContext();
    } catch (error) {
      console.error('❌ Get context failed:', error);
      return { environment: this.environment };
    }
  }

  // Get database statistics
  async getDatabaseStats() {
    if (!this.isInitialized) {
      throw new Error('Database not initialized');
    }

    try {
      return await this.databaseManager.getDatabaseStats();
    } catch (error) {
      console.error('❌ Get database stats failed:', error);
      throw error;
    }
  }

  // Close database
  async close() {
    if (!this.isInitialized) {
      return;
    }

    try {
      await this.databaseManager.close();
      this.isInitialized = false;
      console.log('✅ Database closed');
    } catch (error) {
      console.error('❌ Close database failed:', error);
      throw error;
    }
  }
}

// Create singleton instance
const databaseManager = new DatabaseManager();

export default databaseManager;
